import { set, isFunction, extend, isString, startsWith, get } from 'lodash-es';
import axios from 'axios';
import debug from 'debug';
import Keycloak from 'keycloak-js';

const isNode = typeof window === 'undefined';

if (isNode) set(global, 'window', {});

if (isNode) delete (global as unknown as any).window;

const log = debug('itp:core:auth');

export interface AuthParams {
  realm: string;
  url?: string;
  path?: string;
  clientId: string;
}

export interface UserData {
  id: string;
  userName: string;
  fullName?: string;
  email: string;
}

export interface Authz {
  init(): Promise<any>;
  logout(): void;
  user: UserData;
  accountUrl: string;
  token?: string;
}

export class Auth implements Authz {
  private kc!: Keycloak;
  private $user: UserData;

  readonly url: string;
  readonly options: AuthParams;
  readonly accountUrl: string;

  constructor(options: AuthParams) {
    log('instantiating auth: %o', options);
    this.options = options;
    this.$user = {
      id: '',
      userName: '',
      fullName: undefined,
      email: '',
    };

    this.url = this.urlFromOptions(options);
    this.accountUrl = `${this.url}/realms/${options.realm}/account`;
  }

  private urlFromOptions(options: AuthParams) {
    if (options.url) {
      return options.url;
    }

    const loc = window.location;
    const path = options.path;

    if (isString(path)) {
      if (startsWith(path, 'http')) {
        return path;
      }

      return `${loc.origin}${path}`;
    }

    throw Error('Missing keycloak path.');
  }

  get user(): UserData {
    return this.$user;
  }

  get token() {
    return this.kc?.token;
  }

  private static readonly TOKEN_MIN_VALIDITY_SEC = 70;

  private static readonly REFRESH_INTERVAL_MS = 60_000;

  private refreshIntervalId: ReturnType<typeof setInterval> | null = null;

  init() {
    const self = this;

    return new Promise((resolve, reject) => {
      const kc = new Keycloak({
        url: this.url,
        realm: this.options.realm,
        clientId: this.options.clientId,
      });

      kc.init({
        onLoad: 'login-required',
        responseMode: 'query',
        checkLoginIframe: false,
        enableLogging: true,
        useNonce: false,
      })
        .then((authenticated) => {
          if (authenticated) {
            self.kc = kc;
            log('kc connection success');
            self.setupTokenInterceptor(kc);
            self.setupTokenRefresh(kc);

            self
              .loadUserData()
              .then(() => {
                log('auth success');
                resolve(kc);
              })
              .catch((error) => {
                log('failed to load user data');
                reject(error);
              });
          } else {
            log('not authenticated');
          }
        })
        .catch((error) => {
          log('kc connection error', error);
          reject(error);
        });
    });
  }

  callLogout(fun: Function) {
    const redirectUri = document.URL;
    return fun.call(this.kc, { redirectUri });
  }

  logout() {
    const redirectUri = document.URL;
    log('logging out with redirectUri %s', redirectUri);
    this.stopTokenRefresh();
    this.kc.logout({ redirectUri });
  }

  private reauthenticate(reason: string) {
    log('forcing reauth: %s', reason);
    this.stopTokenRefresh();
    try {
      this.kc.login({ redirectUri: window.location.href });
    } catch (e) {
      log('login redirect failed, hard-reloading instead', e);
      window.location.reload();
    }
  }

  private setupTokenInterceptor(keycloak: Keycloak) {
    axios.interceptors.request.use(
      async (config) => {
        if (keycloak.token) {
          try {
            await keycloak.updateToken(Auth.TOKEN_MIN_VALIDITY_SEC);
          } catch (err) {
            this.reauthenticate('updateToken failed in request interceptor');
            return Promise.reject(err);
          }
          if (config && config.headers && keycloak.token) {
            config.headers.Authorization = `Bearer ${keycloak.token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error),
    );
  }

  private setupTokenRefresh(keycloak: Keycloak) {
    keycloak.onTokenExpired = () => {
      log('access token expired — refreshing');
      keycloak.updateToken(Auth.TOKEN_MIN_VALIDITY_SEC).catch((err) => {
        this.reauthenticate('onTokenExpired refresh failed');
        log('onTokenExpired refresh error', err);
      });
    };

    this.stopTokenRefresh();
    this.refreshIntervalId = setInterval(() => {
      keycloak.updateToken(Auth.TOKEN_MIN_VALIDITY_SEC).catch((err) => {
        this.reauthenticate('background refresh failed');
        log('background refresh error', err);
      });
    }, Auth.REFRESH_INTERVAL_MS);
  }

  private stopTokenRefresh() {
    if (this.refreshIntervalId !== null) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
  }

  private loadUserData() {
    return this.kc.loadUserProfile().then((p) => (this.setUserData(p), p));
  }

  private setUserData(profile: any) {
    extend(this.$user, {
      id: profile.id,
      userName: profile.username,
      fullName: [profile.firstName, profile.lastName]
        .filter((p: any) => !!p)
        .join(' '),
      email: profile.email,
    });

    log('user: %o', this.user);
  }
}

export class FakeAuth implements Authz {
  readonly token: string;
  readonly user: UserData;
  readonly accountUrl!: string;

  constructor(options: {
    token: string;
    userData?: UserData;
    accountUrl?: string;
  }) {
    this.token = options.token;
    this.user = options.userData || {
      id: 'test',
      userName: 'testuser',
      fullName: 'Test Joe',
      email: 'johndoe@domain.com',
    };

    if (isString(options.accountUrl)) {
      this.accountUrl = options.accountUrl;
    }
  }

  init() {
    this.setupTokenInterceptor();
    return Promise.resolve();
  }

  logout() {}

  private setupTokenInterceptor() {
    axios.interceptors.request.use(
      (config) => {
        if (config.headers) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );
  }
}
